import { supabase } from './supabase'
import type { EscopoJornada, ModalidadeEnsino } from '../types/database'

export type FichaItemPayload = {
  alimento_id: string
  quantidade_g: number
  ordem: number
  origem_prato_id: string | null
}

export type FichaSecaoPayload = {
  refeicao: string
  descritivo: string
  prato_ids: string[]
  itens: FichaItemPayload[]
}

export type SalvarFichaTecnicaResult = {
  ficha_ids: string[]
  count: number
}

type SecaoDraftLike = {
  refeicao: string
  descritivo: string
  pratoIds: string[]
  itens: Array<{
    alimento_id: string
    quantidade_g: string
    origem_prato_id: string | null
  }>
}

/** Monta o payload das seções filtrando itens sem alimento. */
export function montarSecoesFichaPayload(secoes: SecaoDraftLike[]): FichaSecaoPayload[] {
  return secoes.map((secao) => ({
    refeicao: secao.refeicao,
    descritivo: secao.descritivo.trim(),
    prato_ids: [...secao.pratoIds],
    itens: secao.itens
      .filter((it) => it.alimento_id && Number(it.quantidade_g) >= 0)
      .map((it, ordem) => ({
        alimento_id: it.alimento_id,
        quantidade_g: Number(it.quantidade_g) || 0,
        ordem,
        origem_prato_id: it.origem_prato_id,
      })),
  }))
}

/**
 * Grava modalidades × seções (pratos + ingredientes) via RPC atômica.
 * Qualquer falha em um item faz rollback de toda a operação no Postgres.
 */
export async function salvarFichaTecnicaCompleta(
  modalidades: ModalidadeEnsino[],
  secoes: FichaSecaoPayload[],
  escopoJornada: EscopoJornada = 'parcial',
): Promise<SalvarFichaTecnicaResult> {
  if (!modalidades.length) throw new Error('Informe ao menos uma modalidade')
  if (!secoes.length) throw new Error('Informe ao menos uma seção de refeição')

  const { data, error } = await supabase.rpc('salvar_ficha_tecnica_completa', {
    p_modalidades: modalidades,
    p_secoes: secoes,
    p_escopo_jornada: escopoJornada,
  })

  if (error) throw new Error(error.message)

  const row = data as { ficha_ids?: string[]; count?: number } | null
  const fichaIds = Array.isArray(row?.ficha_ids) ? row.ficha_ids : []
  if (!fichaIds.length) throw new Error('Erro ao gravar ficha técnica')

  return {
    ficha_ids: fichaIds,
    count: typeof row?.count === 'number' ? row.count : fichaIds.length,
  }
}
